/**
 * Legal & about — the three documents the app renders in-app.
 *
 * `terms`, `privacy` and `about` are edited here and read by the app from the public
 * `/legal` route. They are plain text with a small block-level markdown subset (headings,
 * bullets, numbered clauses, paragraphs) and deliberately **not** HTML: the app puts every
 * block into a text node, so nothing typed here can become markup, a link, or a script on a
 * customer's phone. The preview beside the editor renders exactly those rules.
 *
 * `{{platformName}}` and `{{supportEmail}}` are substituted by the API when a customer reads
 * a document, from Business settings. The editor keeps them as written so one rebrand still
 * rewrites all three documents; the preview fills them in so an operator sees the result.
 *
 * These documents and the Terms/Privacy URLs on Business settings coexist: the app prefers
 * what is published here and falls back to the URLs, so a deployment with a legal website
 * links out and one without writes the text here. Superadmin only.
 */
import { useCallback, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';

import { messageOf } from '../api/errors.js';
import { useAuth } from '../auth/AuthContext.js';
import { adminCan } from '../contracts/permissions.js';
import type { AdminLegalDocument, LegalDocumentKey, PlatformSettings } from '../contracts/types.js';
import { formatDateTime } from '../lib/format.js';
import { Button, Card, ErrorBox, Field, Input, LoadingState } from '../components/ui.js';
import { IconDocument, IconExternalLink, IconEye } from '../components/icons.js';
import { useToast } from '../components/Toast.js';
import { useAsync } from '../hooks/useAsync.js';

/**
 * The closed key set and the token names, mirrored from `@genex/contracts` exactly as
 * `contracts/vocab.ts` mirrors the other runtime constants — the console imports contract
 * *types* only, so none of the package's runtime (zod, @genex/money) is bundled here. The
 * API is the authority: a key it does not know is a 404 rather than a fourth document.
 */
const DOCUMENTS: readonly { readonly key: LegalDocumentKey; readonly label: string; readonly hint: string }[] = [
  { key: 'terms', label: 'Terms of Service', hint: 'The agreement a customer accepts when they open an account.' },
  { key: 'privacy', label: 'Privacy Policy', hint: 'What you collect, why you hold it, and who else sees it.' },
  { key: 'about', label: 'About', hint: 'Who you are and what the app does, in your own words.' },
];

const BODY_TOKENS = ['platformName', 'supportEmail'] as const;

/** Mirrors `LEGAL_BODY_MAX_CHARS`; the API rejects anything past it. */
const BODY_MAX_CHARS = 100_000;

interface Draft {
  readonly title: string;
  readonly body: string;
}

export function LegalPage(): JSX.Element {
  const { api } = useAuth();
  const state = useAsync(
    useCallback(async () => {
      // Business settings come along for the ride so the preview can fill the brand tokens
      // with the same values the API will substitute on the public read.
      const [legal, settings] = await Promise.all([
        api.listLegalDocuments(),
        api.getBusinessSettings(),
      ]);
      return { documents: legal.documents, settings };
    }, [api]),
    [],
  );

  return (
    <>
      <div className="page-head">
        <h1 className="page-head__title">Legal & about</h1>
        <p className="page-head__sub">
          The documents customers read inside the app. Publish one and the app opens it in-app; leave it unpublished and the app falls back to the link in Business settings.
        </p>
      </div>

      {state.loading ? (
        <LoadingState label="Loading documents…" />
      ) : state.error !== null ? (
        <ErrorBox message={state.error} />
      ) : state.data === null ? (
        <ErrorBox message="Those documents are not available." />
      ) : (
        <Editor
          documents={state.data.documents}
          settings={state.data.settings}
          onPublished={(next) =>
            state.setData((prev) =>
              prev === null
                ? prev
                : {
                    ...prev,
                    documents: [
                      ...prev.documents.filter((d) => d.key !== next.key),
                      next,
                    ].sort((a, b) => a.key.localeCompare(b.key)),
                  },
            )
          }
        />
      )}
    </>
  );
}

function Editor({
  documents,
  settings,
  onPublished,
}: {
  documents: readonly AdminLegalDocument[];
  settings: PlatformSettings;
  onPublished: (next: AdminLegalDocument) => void;
}): JSX.Element {
  const { api, session } = useAuth();
  const toast = useToast();
  const canWrite = session !== null && adminCan(session.me.role, 'admins:write');

  const [active, setActive] = useState<LegalDocumentKey>('terms');
  const [saving, setSaving] = useState(false);

  // One draft per document, so switching between the three keeps unsaved edits. Seeded from
  // what the server holds; a document that has never been published starts empty.
  const [drafts, setDrafts] = useState<Record<LegalDocumentKey, Draft>>(() => {
    const seed = {} as Record<LegalDocumentKey, Draft>;
    for (const { key, label } of DOCUMENTS) {
      const stored = documents.find((d) => d.key === key);
      seed[key] = { title: stored?.title ?? label, body: stored?.body ?? '' };
    }
    return seed;
  });

  const stored = documents.find((d) => d.key === active) ?? null;
  const draft = drafts[active];
  const dirty = stored === null ? draft.body.trim() !== '' : draft.title !== stored.title || draft.body !== stored.body;

  const tokenValues = useMemo<Readonly<Record<string, string>>>(
    () => ({ platformName: settings.platformName, supportEmail: settings.supportEmail }),
    [settings.platformName, settings.supportEmail],
  );

  const blocks = useMemo(
    () => parseBody(substitute(draft.body, tokenValues)),
    [draft.body, tokenValues],
  );

  function edit(patch: Partial<Draft>) {
    setDrafts((prev) => ({ ...prev, [active]: { ...prev[active], ...patch } }));
  }

  async function publish() {
    const title = draft.title.trim();
    const body = draft.body.trim();
    if (title === '' || body === '') {
      toast.error('Nothing to publish', 'A document needs a title and some text.');
      return;
    }
    if (body.length > BODY_MAX_CHARS) {
      toast.error('That document is too long', `Keep it under ${BODY_MAX_CHARS.toLocaleString()} characters.`);
      return;
    }
    setSaving(true);
    try {
      const next = await api.publishLegalDocument(active, { title, body });
      setDrafts((prev) => ({ ...prev, [active]: { title: next.title, body: next.body } }));
      onPublished(next);
      toast.success('Published', 'Customers see this the next time they open the document.');
    } catch (err) {
      toast.error('Could not publish', messageOf(err));
    } finally {
      setSaving(false);
    }
  }

  return (
    <div className="tpl-editor">
      <Card>
        <div className="seg">
          {DOCUMENTS.map(({ key, label }) => {
            const unsaved = key === active ? dirty : hasUnsavedEdits(drafts[key], documents.find((d) => d.key === key) ?? null);
            return (
              <button
                key={key}
                className={`seg__btn${key === active ? ' seg__btn--on' : ''}`}
                onClick={() => setActive(key)}
              >
                <IconDocument size={15} /> {label}
                {unsaved && <span title="Unsaved changes">•</span>}
              </button>
            );
          })}
        </div>

        <p className="muted" style={{ fontSize: 13, marginBottom: 0 }}>
          {DOCUMENTS.find((d) => d.key === active)?.hint}{' '}
          {stored === null ? (
            <strong>Not published yet, so the app falls back to your configured URL.</strong>
          ) : (
            <>Last published {formatDateTime(stored.updatedAt)}.</>
          )}
        </p>

        <Field label="Title (shown at the top of the document in the app)" htmlFor="legal-title">
          <Input
            id="legal-title"
            value={draft.title}
            maxLength={160}
            disabled={!canWrite}
            placeholder="Terms of Service"
            onChange={(e) => edit({ title: e.target.value })}
          />
        </Field>

        <div className="tpl-vars">
          <span className="faint" style={{ fontSize: 12 }}>Filled in when a customer reads it:</span>
          {BODY_TOKENS.map((token) => (
            <code key={token} className="tpl-token tpl-token--global">{`{{${token}}}`}</code>
          ))}
          <span className="faint" style={{ fontSize: 12 }}>Formatting:</span>
          <code className="tpl-token"># Heading</code>
          <code className="tpl-token">## Sub-heading</code>
          <code className="tpl-token">- Bullet</code>
          <code className="tpl-token">1. Clause</code>
        </div>
      </Card>

      <div className="tpl-editor__grid">
        <Card
          flush
          title="Document"
          actions={
            <span className={draft.body.length > BODY_MAX_CHARS ? 'form-error' : 'faint'} style={{ fontSize: 12 }}>
              {draft.body.length.toLocaleString()} / {BODY_MAX_CHARS.toLocaleString()} characters
            </span>
          }
        >
          <textarea
            className="tpl-html"
            spellCheck
            value={draft.body}
            disabled={!canWrite}
            placeholder={'# Terms of Service\n\nParagraphs are separated by a blank line.\n\n## 1. Your account\n\n1. Clauses can be numbered.\n- Or bulleted.'}
            onChange={(e) => edit({ body: e.target.value })}
          />
        </Card>

        <Card
          flush
          title={
            <span className="tpl-preview__title">
              <IconEye size={16} /> How the app renders it
            </span>
          }
        >
          <div className="tpl-preview">
            <div className="tpl-preview__subject">
              <span className="faint">Title</span> {substitute(draft.title, tokenValues)}
            </div>
            <div style={{ padding: '16px 18px', overflowY: 'auto', maxHeight: 520, lineHeight: 1.6 }}>
              {blocks.length === 0 ? (
                <p className="muted" style={{ margin: 0, fontSize: 13 }}>
                  Nothing to show yet. Paste or write the document on the left.
                </p>
              ) : (
                blocks.map((block, index) => <PreviewBlock key={index} block={block} />)
              )}
            </div>
          </div>
        </Card>
      </div>

      <Card>
        <div className="tpl-actions">
          <div className="muted" style={{ fontSize: 13, flex: 1, minWidth: 260 }}>
            {canWrite
              ? 'These ship as starting documents. Replace them with text your own counsel has read before you launch.'
              : 'Your role can view but not change these documents.'}
          </div>
          <Link to="/settings/business" className="backlink">
            Terms & Privacy URLs <IconExternalLink size={14} />
          </Link>
          <Button variant="primary" loading={saving} disabled={!canWrite || !dirty} onClick={() => void publish()}>
            Publish {DOCUMENTS.find((d) => d.key === active)?.label}
          </Button>
        </div>
      </Card>
    </div>
  );
}

function hasUnsavedEdits(draft: Draft | undefined, stored: AdminLegalDocument | null): boolean {
  if (draft === undefined) return false;
  if (stored === null) return draft.body.trim() !== '';
  return draft.title !== stored.title || draft.body !== stored.body;
}

// ── The preview renderer ───────────────────────────────────────────────────────

/**
 * The block rules, mirrored from the contract's `parseLegalBody`.
 *
 * A copy rather than an import for the same reason `contracts/vocab.ts` is a copy: the
 * console bundles no contract runtime. The app is what customers see, so `parseLegalBody`
 * is the definition and this follows it; the pure test that pins the rules lives beside it
 * in the contracts package.
 */
type Block =
  | { kind: 'heading'; level: 1 | 2; text: string }
  | { kind: 'paragraph'; text: string }
  | { kind: 'bullet'; text: string }
  | { kind: 'ordered'; marker: string; text: string };

const ORDERED = /^(\d{1,3}[.)])\s+(.*)$/;

function parseBody(body: string): Block[] {
  const blocks: Block[] = [];
  let paragraph: string[] = [];
  let openItem = false;

  const flush = () => {
    if (paragraph.length > 0) {
      blocks.push({ kind: 'paragraph', text: paragraph.join(' ') });
      paragraph = [];
    }
    openItem = false;
  };

  for (const raw of body.replace(/\r\n?/g, '\n').split('\n')) {
    const line = raw.trim();
    if (line === '') {
      flush();
      continue;
    }
    if (line.startsWith('## ')) {
      flush();
      blocks.push({ kind: 'heading', level: 2, text: line.slice(3).trim() });
      continue;
    }
    if (line.startsWith('# ')) {
      flush();
      blocks.push({ kind: 'heading', level: 1, text: line.slice(2).trim() });
      continue;
    }
    if (line.startsWith('- ') || line.startsWith('* ')) {
      flush();
      blocks.push({ kind: 'bullet', text: line.slice(2).trim() });
      openItem = true;
      continue;
    }
    const ordered = ORDERED.exec(line);
    if (ordered !== null) {
      flush();
      blocks.push({ kind: 'ordered', marker: ordered[1] ?? '', text: (ordered[2] ?? '').trim() });
      openItem = true;
      continue;
    }
    const previous = blocks[blocks.length - 1];
    if (openItem && previous !== undefined) {
      blocks[blocks.length - 1] = { ...previous, text: `${previous.text} ${line}` };
      continue;
    }
    paragraph.push(line);
  }

  flush();
  return blocks;
}

/** `{{token}}` substitution, matching the API's: an unknown name is left as typed. */
function substitute(text: string, values: Readonly<Record<string, string>>): string {
  return text.replace(/\{\{\s*([A-Za-z][A-Za-z0-9_]*)\s*\}\}/g, (whole, name: string) =>
    Object.prototype.hasOwnProperty.call(values, name) ? (values[name] ?? whole) : whole,
  );
}

function PreviewBlock({ block }: { block: Block }): JSX.Element {
  switch (block.kind) {
    case 'heading':
      return block.level === 1 ? (
        <h2 style={{ fontSize: 18, margin: '18px 0 8px' }}>{block.text}</h2>
      ) : (
        <h3 style={{ fontSize: 14.5, margin: '16px 0 6px' }}>{block.text}</h3>
      );
    case 'bullet':
      return (
        <div style={{ display: 'flex', gap: 8, fontSize: 13.5, margin: '4px 0' }}>
          <span className="faint">•</span>
          <span>{block.text}</span>
        </div>
      );
    case 'ordered':
      return (
        <div style={{ display: 'flex', gap: 8, fontSize: 13.5, margin: '4px 0' }}>
          <span className="faint mono">{block.marker}</span>
          <span>{block.text}</span>
        </div>
      );
    default:
      return <p style={{ fontSize: 13.5, margin: '8px 0' }}>{block.text}</p>;
  }
}
